import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AppTablePagination from "../../component/common/appTablePagination";
import SearchIcon from "@mui/icons-material/Search";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TagIcon from "@mui/icons-material/Tag";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { useTranslation } from "react-i18next";
import WhmFilterFieldsGrid from "../../../components/whm/WhmFilterFieldsGrid";
import itemControlService from "../../../services/ItemControlService";
import {
  BRAND,
  CONTROL_HEIGHT,
  NJIM,
  glassCardSx,
  pageShellSx,
  searchFieldSx,
  softFieldSx,
  whmFilterHeaderSx,
  whmFilterLabelSx,
  whmFilterSelectSx,
} from "../../../theme/njimTheme";
import saleOrderServices from "../../../services/SalesOrderService";
import customerServices from "../../../services/CustomerService";
import { showToast } from "../../component/common/toastNotification";

/* ─── types ─── */
interface ProductItem {
  id: string;
  code: string;
  name: string;
  stock: number;
  stockSite: string;
}

interface ControlRelease {
  id: string;
  itemId: string;
  itemCode: string;
  itemDescription: string;
  quantityReleased: number;
  stockSite: string;
  controlLogic: "None" | "Consumption" | "Customer";
  maxQtyPerCustomer?: number;
  authorizedCustomerIds: string[];
  batchNumber?: string;
}

interface ModalBatch {
  lotNumber: string;
  expirationDate: string;
}

interface ItemGroup {
  /** Stable key: one row per (item code, stock site) */
  rowKey: string;
  code: string;
  description: string;
  representativeId: string;
  totalQty: number;
  stockSite: string;
  control?: ControlRelease;
}

interface CustomerOption {
  id: string;
  name: string;
  customerNo: string;
}

type ModalState = {
  mode: "add" | "edit";
  group: ItemGroup;
  control?: ControlRelease;
} | null;

type ControlLogicType = "None" | "Consumption" | "Customer";

// Used when a control record EXISTS with logic = None (item is released, no restriction)
const CONTROL_BADGE: Record<ControlLogicType, { bg: string; color: string }> = {
  None: { bg: "rgba(22,163,74,0.08)", color: NJIM.success },
  Consumption: { bg: "rgba(245,158,11,0.1)", color: NJIM.warning },
  Customer: { bg: "rgba(99,102,241,0.1)", color: NJIM.info },
};

// Used when there is NO control record at all
const NO_CONTROL_BADGE = { bg: "rgba(226,232,240,0.6)", color: NJIM.textMuted };

const CONTROL_LOGIC_KEY: Record<ControlLogicType, string> = {
  None: "itemControl.noRestriction",
  Consumption: "itemControl.consumptionRestriction",
  Customer: "itemControl.customerRestriction",
};


/* ─── component ─── */
const ItemControlRelease: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  /* ── state ── */
  const [activeTab, setActiveTab] = useState<"available" | "active">("available");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [controlStatusFilter, setControlStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [controls, setControls] = useState<ControlRelease[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);

  /* ── confirm delete dialog ── */
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  /* ── pagination ── */
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(25);

  /* ── modal state ── */
  const [modal, setModal] = useState<ModalState>(null);
  // AND logic: each restriction is independently toggled
  const [formConsumptionEnabled, setFormConsumptionEnabled] = useState(false);
  const [formCustomerEnabled, setFormCustomerEnabled] = useState(false);
  const [formMaxQty, setFormMaxQty] = useState("");
  const [formCustomers, setFormCustomers] = useState<string[]>([]);
  // Sites: which sites to apply the control to
  const [formSites, setFormSites] = useState<string[]>([]);
  // Customer search within the modal — client-side for the preloaded list + server-side debounce
  const [customerSearch, setCustomerSearch] = useState("");
  const [liveCustomers, setLiveCustomers] = useState<CustomerOption[]>([]);
  const customerSearchLoading = false;

  // Batch selection in the modal
  const [formBatchMode, setFormBatchMode] = useState<"all" | "specific">("all");
  const [formSelectedBatches, setFormSelectedBatches] = useState<string[]>([]);
  const [modalBatches, setModalBatches] = useState<ModalBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  /* ── debounce table search ── */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim().toLowerCase()), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  /* ── customer search in modal — pure local filter, matching mobile app logic ── */
  useEffect(() => {
    const query = customerSearch.trim();
    if (query.length < 1) {
      setLiveCustomers([]);
      return;
    }
    const q = query.toLowerCase();
    const matches = customers
      .filter(
        (c) =>
          formCustomers.includes(c.id) ||
          c.name.toLowerCase().includes(q) ||
          c.customerNo.toLowerCase().includes(q)
      )
      .slice(0, 30);
    setLiveCustomers(matches);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearch, customers, formCustomers]);


  /* ── load data ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, ctrlRes] = await Promise.allSettled([
        saleOrderServices.getAllProducts(5000),
        itemControlService.getAll(),
      ]);

      if (prodRes.status === "fulfilled") {
        const raw = prodRes.value?.data;
        const arr: Record<string, unknown>[] = Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw)
          ? raw
          : [];

        setProducts(
          arr.map((p) => ({
            id: String(p.id ?? p.Id ?? ""),
            code: String(p.code ?? p.Code ?? "").trim(),
            name: String(p.name ?? p.Name ?? p.title ?? p.Title ?? ""),
            stock: Number(p.stock ?? p.Stock ?? 0),
            stockSite: String(p.stockSite ?? p.StockSite ?? "").trim(),
          }))
        );
      }

      if (ctrlRes.status === "fulfilled") {
        const arr: Record<string, unknown>[] = Array.isArray(ctrlRes.value?.data)
          ? ctrlRes.value.data
          : [];
        setControls(
          arr.map((c) => ({
            id: String(c.id ?? ""),
            itemId: String(c.itemId ?? ""),
            itemCode: String(c.itemCode ?? "").trim(),
            itemDescription: String(c.itemDescription ?? ""),
            quantityReleased: Number(c.quantityReleased ?? 0),
            stockSite: String(c.stockSite ?? ""),
            controlLogic: String(c.controlLogic ?? "None") as ControlLogicType,
            maxQtyPerCustomer: c.maxQtyPerCustomer ? Number(c.maxQtyPerCustomer) : undefined,
            authorizedCustomerIds: Array.isArray(c.authorizedCustomerIds)
              ? (c.authorizedCustomerIds as string[])
              : [],
            batchNumber: c.batchNumber ? String(c.batchNumber) : undefined,
          }))
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload customers whenever the modal opens so stale HMR state is never an issue.
  // Skip the reload if data is already fresh (customers already loaded for this modal session).
  const customersLoadedRef = React.useRef(false);
  useEffect(() => {
    // Load on mount
    if (!customersLoadedRef.current) {
      customersLoadedRef.current = true;
      loadCustomers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Re-load whenever the modal opens to ensure latest name mapping is applied
    if (modal) {
      loadCustomers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.mode, modal?.group?.code]);

  async function loadCustomers() {
    // Mobile app mapping: { id, code: customerCode ?? code, name: customerName ?? name }
    const mapCustomer = (c: Record<string, unknown>): CustomerOption => ({
      id: String(c.id ?? ""),
      name: String(
        (c.customerName as string | undefined) ??
        (c.CustomerName as string | undefined) ??
        ""
      ),
      customerNo: String(
        (c.customerCode as string | undefined) ??
        (c.CustomerCode as string | undefined) ??
        (c.code as string | undefined) ??
        ""
      ),
    });

    try {
      // Primary: unpaged GET /api/customers — returns flat array of all customers
      const res = await customerServices.getAllCustomersUnpaged();
      const raw: Record<string, unknown>[] = Array.isArray(res?.data)
        ? (res.data as Record<string, unknown>[])
        : Array.isArray((res?.data as { items?: unknown[] })?.items)
        ? ((res!.data as { items: Record<string, unknown>[] }).items)
        : [];
      if (raw.length > 0) {
        setCustomers(raw.map(mapCustomer));
        return;
      }
    } catch {
      // fall through to paginated fallback
    }

    try {
      // Fallback: paginated search with empty keyword = all customers up to 5000
      const res2 = await customerServices.getAllCustomers(5000);
      const paged = res2?.data as { items?: Record<string, unknown>[] } | undefined;
      const raw2: Record<string, unknown>[] = Array.isArray(paged?.items) ? paged!.items! : [];
      setCustomers(raw2.map(mapCustomer));
    } catch {
      // keep existing list
    }
  }

  /* ── derived: sites from product rows only (each row is code + site) ── */
  const sites = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      const code = (p.code || "").trim();
      const site = (p.stockSite || "").trim();
      if (code && site) set.add(site);
    });
    return Array.from(set).sort();
  }, [products]);

  /* ── derived: item groups ── */
  // Build a map: itemCode → Map<site, ControlRelease> so we can pick the right control per site
  const ctrlsByCodeAndSite = useMemo(() => {
    const outer = new Map<string, Map<string, ControlRelease>>();
    controls.forEach((c) => {
      const key = (c.itemCode || "").trim().toLowerCase();
      if (!key) return;
      if (!outer.has(key)) outer.set(key, new Map());
      const siteKey = (c.stockSite || "").trim().toLowerCase();
      outer.get(key)!.set(siteKey, c);
    });
    return outer;
  }, [controls]);

  const itemGroups = useMemo((): ItemGroup[] => {
    // One row per (item code, stock site) so site filter = "all product lines at this warehouse site"
    const map = new Map<
      string,
      { code: string; description: string; representativeId: string; totalQty: number; stockSite: string; rowKey: string }
    >();
    products.forEach((p) => {
      const code = (p.code || "").trim();
      if (!code) return;
      const codeKey = code.toLowerCase();
      const siteRaw = (p.stockSite || "").trim();
      const siteNorm = siteRaw.toLowerCase();
      const rowKey = `${codeKey}|${siteNorm}`;
      if (!map.has(rowKey)) {
        map.set(rowKey, {
          code,
          description: p.name,
          representativeId: p.id,
          totalQty: 0,
          stockSite: siteRaw,
          rowKey,
        });
      }
      map.get(rowKey)!.totalQty += p.stock;
    });

    return Array.from(map.values())
      .map((g) => {
        const codeKey = g.code.toLowerCase();
        const ctrl = ctrlsByCodeAndSite.get(codeKey)?.get(g.stockSite.trim().toLowerCase());
        return {
          rowKey: g.rowKey,
          code: g.code,
          description: g.description,
          representativeId: g.representativeId,
          totalQty: g.totalQty,
          stockSite: g.stockSite,
          control: ctrl,
        };
      })
      .sort((a, b) => {
        const bySite = a.stockSite.localeCompare(b.stockSite);
        if (bySite !== 0) return bySite;
        return a.code.localeCompare(b.code);
      });
  }, [products, ctrlsByCodeAndSite]);

  /* ── stats ── */
  const stats = useMemo(() => {
    const uniqueItems = itemGroups.length;
    // Count ALL items that have any control record (None = no restriction but still released/controlled)
    const activeControls = itemGroups.filter((g) => g.control !== undefined).length;
    const customerRestricted = controls.filter((c) => c.controlLogic === "Customer").length;
    return { uniqueItems, activeControls, customerRestricted };
  }, [itemGroups, controls]);

  /* ── filtered groups ── */
  const filteredGroups = useMemo(() => {
    let groups = itemGroups;

    if (activeTab === "active") {
      groups = groups.filter((g) => g.control !== undefined);
    }

    if (debouncedSearch) {
      groups = groups.filter(
        (g) =>
          g.code.toLowerCase().includes(debouncedSearch) ||
          g.description.toLowerCase().includes(debouncedSearch)
      );
    }

    if (siteFilter !== "all") {
      const siteNorm = siteFilter.trim().toLowerCase();
      groups = groups.filter((g) => g.stockSite.trim().toLowerCase() === siteNorm);
    }

    if (controlStatusFilter === "with") {
      groups = groups.filter((g) => g.control !== undefined);
    } else if (controlStatusFilter === "none") {
      groups = groups.filter((g) => g.control === undefined);
    }

    return groups;
  }, [itemGroups, activeTab, debouncedSearch, siteFilter, controlStatusFilter]);

  /* ── modal helpers ── */

  // Compute which sites this item code exists at
  const modalAvailableSites = useMemo(() => {
    if (!modal) return [];
    const code = modal.group.code.trim().toLowerCase();
    const siteSet = new Set<string>();
    products.forEach((p) => {
      if (p.code.trim().toLowerCase() === code && p.stockSite) siteSet.add(p.stockSite);
    });
    return Array.from(siteSet).sort();
  }, [modal, products]);

  // Derive ControlLogic from the two independent toggles
  const getFormControlLogic = (): ControlLogicType => {
    if (formCustomerEnabled) return "Customer"; // Customer takes priority; maxQty also sent for AND logic
    if (formConsumptionEnabled) return "Consumption";
    return "None";
  };

  const openAddModal = (group: ItemGroup) => {
    setFormConsumptionEnabled(false);
    setFormCustomerEnabled(false);
    setFormMaxQty("");
    setFormCustomers([]);
    setFormBatchMode("all");
    setFormSelectedBatches([]);
    setModalBatches([]);
    // Default "Add control" to this row's site only (user can enable more sites in the modal)
    const rowSite = group.stockSite.trim();
    const allSitesForCode = Array.from(
      new Set(
        products
          .filter((p) => p.code.trim().toLowerCase() === group.code.trim().toLowerCase())
          .map((p) => p.stockSite.trim())
          .filter(Boolean)
      )
    );
    setFormSites(rowSite ? [rowSite] : allSitesForCode);
    setModal({ mode: "add", group });
    fetchModalBatches(group.code);
  };

  const openEditModal = (group: ItemGroup, control: ControlRelease) => {
    // AND logic: if Customer logic AND maxQtyPerCustomer is set → both restrictions active
    const hasConsumption = control.controlLogic === "Consumption" || (control.controlLogic === "Customer" && !!control.maxQtyPerCustomer);
    setFormConsumptionEnabled(hasConsumption);
    setFormCustomerEnabled(control.controlLogic === "Customer");
    setFormMaxQty(control.maxQtyPerCustomer ? String(control.maxQtyPerCustomer) : "");
    setFormCustomers(control.authorizedCustomerIds ?? []);
    // Collect ALL sites that have a control record for this item code (ticket 9)
    const codeKey = group.code.trim().toLowerCase();
    const siteSet = new Set<string>();
    controls
      .filter((c) => c.itemCode.trim().toLowerCase() === codeKey && c.stockSite)
      .forEach((c) => siteSet.add(c.stockSite));
    const allControlledSites = Array.from(siteSet);
    setFormSites(allControlledSites.length > 0 ? allControlledSites : (control.stockSite ? [control.stockSite] : []));
    // Pre-fill batch mode from existing control
    if (control.batchNumber) {
      setFormBatchMode("specific");
      setFormSelectedBatches([control.batchNumber]);
    } else {
      setFormBatchMode("all");
      setFormSelectedBatches([]);
    }
    setModalBatches([]);
    setModal({ mode: "edit", group, control });
    fetchModalBatches(group.code);
  };

  const fetchModalBatches = async (productCode: string) => {
    if (!productCode) return;
    setBatchesLoading(true);
    try {
      const res = await itemControlService.getBatchesForItem(productCode);
      const raw: Record<string, unknown>[] = Array.isArray(res?.data) ? res.data : [];
      const seen = new Set<string>();
      const batches: ModalBatch[] = [];
      raw.forEach((b) => {
        const lot = String(b.lotNumber ?? "").trim();
        if (lot && !seen.has(lot)) {
          seen.add(lot);
          batches.push({ lotNumber: lot, expirationDate: String(b.expirationDate ?? "") });
        }
      });
      setModalBatches(batches);
    } catch {
      setModalBatches([]);
    } finally {
      setBatchesLoading(false);
    }
  };

  const closeModal = () => {
    setModal(null);
    setFormConsumptionEnabled(false);
    setFormCustomerEnabled(false);
    setFormMaxQty("");
    setFormCustomers([]);
    setFormSites([]);
    setCustomerSearch("");
    setLiveCustomers([]);
    setFormBatchMode("all");
    setFormSelectedBatches([]);
    setModalBatches([]);
  };

  const isModalValid = () => {
    if (formConsumptionEnabled && (formMaxQty.trim() === "" || Number(formMaxQty) <= 0)) return false;
    if (formCustomerEnabled && formCustomers.length === 0) return false;
    if (formSites.length === 0) return false;
    if (formBatchMode === "specific" && formSelectedBatches.length === 0) return false;
    return true;
  };

  const handleSubmitControl = async () => {
    if (actionLoading) return;
    if (!modal || !isModalValid()) return;
    setActionLoading(true);
    try {
      const logic = getFormControlLogic();
      const qty = modal.group.totalQty;

      // Batch list to apply to: specific batches OR a single "all batches" placeholder
      const batchesToApply: Array<string | undefined> =
        formBatchMode === "specific" && formSelectedBatches.length > 0
          ? formSelectedBatches
          : [undefined]; // undefined = all batches

      if (modal.mode === "add") {
        // Find one representative product per selected site
        const siteProducts: ProductItem[] = [];
        const seenSites = new Set<string>();
        const code = modal.group.code.trim().toLowerCase();
        const siteMatchesForm = (stockSite: string) =>
          formSites.some((s) => s.trim().toLowerCase() === stockSite.trim().toLowerCase());
        for (const p of products) {
          const siteNorm = p.stockSite.trim().toLowerCase();
          if (p.code.trim().toLowerCase() === code && siteMatchesForm(p.stockSite) && !seenSites.has(siteNorm)) {
            siteProducts.push(p);
            seenSites.add(siteNorm);
          }
        }
        if (siteProducts.length === 0) {
          siteProducts.push({ id: modal.group.representativeId, code: modal.group.code, name: modal.group.description, stock: qty, stockSite: modal.group.stockSite });
        }

        // Create one control per (site × batch)
        await Promise.all(
          siteProducts.flatMap((p) =>
            batchesToApply.map((batchNumber) =>
              itemControlService.create({
                itemId: p.id,
                quantityReleased: qty,
                controlLogic: logic,
                maxQtyPerCustomer: formConsumptionEnabled && formMaxQty ? Number(formMaxQty) : undefined,
                authorizedCustomerIds: formCustomerEnabled ? formCustomers : [],
                batchNumber,
              })
            )
          )
        );
        showToast(t("itemControl.addSuccess", { defaultValue: "Control added successfully" }), "success");
      } else if (modal.mode === "edit" && modal.control) {
        await itemControlService.update(modal.control.id, {
          id: modal.control.id,
          itemId: modal.group.representativeId,
          quantityReleased: qty,
          controlLogic: logic,
          maxQtyPerCustomer: formConsumptionEnabled && formMaxQty ? Number(formMaxQty) : undefined,
          authorizedCustomerIds: formCustomerEnabled ? formCustomers : [],
          batchNumber: batchesToApply[0],
        });
        showToast(t("itemControl.updateSuccess", { defaultValue: "Control updated successfully" }), "success");
      }
      closeModal();
      loadData();
    } catch {
      showToast(t("itemControl.saveError", { defaultValue: "Failed to save control" }), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveControl = async () => {
    if (!confirmDeleteId) return;
    setActionLoading(true);
    try {
      await itemControlService.remove(confirmDeleteId);
      showToast(t("itemControl.removeSuccess", { defaultValue: "Control removed" }), "success");
      loadData();
    } catch {
      showToast(t("itemControl.removeError", { defaultValue: "Failed to remove control" }), "error");
    } finally {
      setActionLoading(false);
      setConfirmDeleteId(null);
    }
  };

  const toggleCustomer = (id: string) => {
    setFormCustomers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const thSx = {
    fontWeight: 600,
    color: NJIM.textSecondary,
    fontSize: "12px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.4px",
    py: 1.2,
    borderBottom: "1px solid rgba(226,232,240,0.8)",
    background: NJIM.muted,
  };

  const tdSx = {
    fontSize: "13px",
    color: NJIM.textPrimary,
    py: 1.4,
    borderBottom: "1px solid rgba(226,232,240,0.5)",
  };

  /* ─── render ─── */
  return (
    <Box sx={pageShellSx} dir={isRTL ? "rtl" : "ltr"}>
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>

        {/* ── Header ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: NJIM.walletHero, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <InventoryOutlinedIcon sx={{ color: NJIM.white, fontSize: 20 }} />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: NJIM.textPrimary }}>
                {t("itemControl.title", { defaultValue: "Item Control & Release" })}
              </Typography>
              <Chip label="WHM" size="small" sx={{ fontWeight: 700, fontSize: 11, background: NJIM.walletHero, color: NJIM.white, height: 22, borderRadius: "6px" }} />
            </Box>
            <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mt: 0.2 }}>
              {t("itemControl.subtitle", { defaultValue: "Browse inventory items and add controls per item as needed" })}
            </Typography>
          </Box>
        </Box>

        {/* ── Summary Cards ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
          {[
            {
              label: t("itemControl.uniqueItems"),
              value: stats.uniqueItems,
              icon: <InventoryOutlinedIcon sx={{ fontSize: 20, color: NJIM.info }} />,
              iconBg: "rgba(99,102,241,0.08)",
              valueColor: NJIM.info,
            },
            {
              label: t("itemControl.activeControls"),
              value: stats.activeControls,
              icon: <CheckCircleOutlineIcon sx={{ fontSize: 20, color: NJIM.success }} />,
              iconBg: "rgba(16,185,129,0.08)",
              valueColor: NJIM.success,
            },
            {
              label: t("itemControl.restrictedAccess"),
              value: stats.customerRestricted,
              icon: <PersonOutlineIcon sx={{ fontSize: 20, color: NJIM.info }} />,
              iconBg: "rgba(59,130,246,0.08)",
              valueColor: NJIM.info,
            },
          ].map((card) => (
            <Box key={card.label} sx={{ ...glassCardSx, p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ width: 42, height: 42, borderRadius: "10px", background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, fontWeight: 500 }}>{card.label}</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 700, color: card.valueColor, lineHeight: 1.2 }}>
                  {loading ? "—" : card.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Filters ── */}
        <Box sx={{ ...glassCardSx, p: 2.5, mb: 2.5, overflow: "visible" }}>
          <Box sx={whmFilterHeaderSx}>
            <FilterAltOutlinedIcon sx={{ color: NJIM.textSecondary, fontSize: 16 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary }}>{t("itemControl.filters")}</Typography>
          </Box>
          <WhmFilterFieldsGrid>
            <Box>
              <Typography sx={whmFilterLabelSx}>{t("itemControl.search")}</Typography>
              <Box
                component="form"
                onSubmit={(e) => e.preventDefault()}
                sx={{ ...searchFieldSx, ...softFieldSx, px: 1.2, height: CONTROL_HEIGHT, boxSizing: "border-box" }}
              >
                <SearchIcon sx={{ color: NJIM.textMuted, fontSize: 16, flexShrink: 0 }} />
                <InputBase
                  placeholder={t("itemControl.searchPlaceholder")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ fontSize: 13, flex: 1 }}
                />
              </Box>
            </Box>
            <Box>
              <Typography sx={whmFilterLabelSx}>{t("itemControl.site")}</Typography>
              <Select
                fullWidth
                size="small"
                value={siteFilter}
                onChange={(e: SelectChangeEvent) => setSiteFilter(e.target.value)}
                sx={whmFilterSelectSx}
              >
                <MenuItem value="all">{t("itemControl.allSites")}</MenuItem>
                {sites.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography sx={whmFilterLabelSx}>{t("itemControl.controlStatus")}</Typography>
              <Select
                fullWidth
                size="small"
                value={controlStatusFilter}
                onChange={(e: SelectChangeEvent) => setControlStatusFilter(e.target.value)}
                sx={whmFilterSelectSx}
              >
                <MenuItem value="all">{t("itemControl.allItems")}</MenuItem>
                <MenuItem value="with">{t("itemControl.withControl")}</MenuItem>
                <MenuItem value="none">{t("itemControl.noControl")}</MenuItem>
              </Select>
            </Box>
          </WhmFilterFieldsGrid>
        </Box>

        {/* ── Tabs ── */}
        <Box sx={{ display: "flex", width: "fit-content", ...glassCardSx, p: 0.5, mb: 2.5 }}>
          {([
            { key: "available" as const, label: t("itemControl.availableItems"), icon: <LayersOutlinedIcon sx={{ fontSize: 15 }} />, count: itemGroups.length },
            { key: "active" as const, label: t("itemControl.activeControls"), icon: <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />, count: stats.activeControls },
          ]).map((tab) => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 2.5,
                py: 1,
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
                transition: "all 0.15s",
                background: activeTab === tab.key ? `${NJIM.walletHero}` : "transparent",
                color: activeTab === tab.key ? NJIM.white : NJIM.textSecondary,
                userSelect: "none",
              }}
            >
              {tab.icon}
              {tab.label}
              <Box
                sx={{
                  px: 0.8,
                  py: 0.1,
                  borderRadius: "5px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: activeTab === tab.key ? "rgba(255,255,255,0.25)" : "rgba(100,116,139,0.12)",
                  color: activeTab === tab.key ? NJIM.white : NJIM.textSecondary,
                  minWidth: 20,
                  textAlign: "center",
                }}
              >
                {tab.count}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Table ── */}
        <Box sx={{ ...glassCardSx, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid rgba(226,232,240,0.8)" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: NJIM.textPrimary }}>
              {t("itemControl.inventoryFromSage")}
            </Typography>
            <Typography sx={{ fontSize: 12, color: NJIM.textMuted, mt: 0.2 }}>
              {loading
                ? t("common.loading")
                : t("itemControl.showingItems", { filtered: filteredGroups.length, total: itemGroups.length })}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={32} sx={{ color: BRAND }} />
            </Box>
          ) : filteredGroups.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ fontSize: 14, color: NJIM.textMuted }}>{t("itemControl.noItemsFound")}</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t("itemControl.colItemCode")}</TableCell>
                  <TableCell sx={thSx}>{t("itemControl.colItemDesc")}</TableCell>
                  <TableCell sx={thSx}>{t("itemControl.colSite")}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "end" }}>{t("itemControl.colTotalQty")}</TableCell>
                  <TableCell sx={thSx}>{t("itemControl.controlStatus")}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "center" }}>{t("itemControl.colAction")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGroups.slice(tablePage * tableRowsPerPage, (tablePage + 1) * tableRowsPerPage).map((group) => {
                  const ctrl = group.control;
                  const logic: ControlLogicType = ctrl?.controlLogic ?? "None";
                  // Use colored badge only when a control record actually exists; gray when no record
                  const badge = ctrl ? CONTROL_BADGE[logic] : NO_CONTROL_BADGE;
                  const badgeLabel = ctrl ? t(CONTROL_LOGIC_KEY[logic]) : t("itemControl.noControl");

                  return (
                    <TableRow
                      key={group.rowKey}
                      sx={{ "&:hover": { background: "rgba(248,250,252,0.8)" }, transition: "background 0.12s" }}
                    >
                      <TableCell sx={{ ...tdSx, fontWeight: 700, color: BRAND }}>{group.code}</TableCell>
                      <TableCell sx={tdSx}>{group.description}</TableCell>
                      <TableCell sx={{ ...tdSx, fontWeight: 600, color: NJIM.textSecondary }}>{group.stockSite || "—"}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "end", fontWeight: 700, color: BRAND }}>
                        {group.totalQty.toLocaleString("en-US")}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.2, py: 0.4, borderRadius: "6px", fontSize: 11, fontWeight: 600, ...badge, width: "fit-content" }}>
                            {badgeLabel}
                          </Box>
                          {ctrl?.batchNumber && (
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: "5px", fontSize: 10, fontWeight: 600, background: "rgba(99,102,241,0.08)", color: NJIM.info, width: "fit-content" }}>
                              <LayersOutlinedIcon sx={{ fontSize: 11 }} />
                              {ctrl.batchNumber}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                        {!ctrl ? (
                          <Button
                            size="small"
                            onClick={() => openAddModal(group)}
                            sx={{
                              textTransform: "none",
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: "7px",
                              background: `${NJIM.walletHero}`,
                              color: NJIM.white,
                              px: 2,
                              py: 0.5,
                              "&:hover": { opacity: 0.9 },
                            }}
                          >
                            + {t("itemControl.addControl")}
                          </Button>
                        ) : (
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openEditModal(group, ctrl)}
                              sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "7px", borderColor: "rgba(22,45,58,0.3)", color: BRAND, px: 1.8, py: 0.5 }}
                            >
                              {t("itemControl.edit")}
                            </Button>
                            <Button
                              size="small"
                              disabled={actionLoading}
                              onClick={() => setConfirmDeleteId(ctrl.id)}
                              sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "7px", color: NJIM.danger, px: 1.8, py: 0.5, "&:hover": { background: "rgba(239,68,68,0.06)" } }}
                            >
                              {t("itemControl.remove")}
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {!loading && filteredGroups.length > 0 && (
            <AppTablePagination
              component="div"
              count={filteredGroups.length}
              page={tablePage}
              rowsPerPage={tableRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => setTablePage(p)}
              onRowsPerPageChange={(e) => {
                setTableRowsPerPage(parseInt(e.target.value, 10));
                setTablePage(0);
              }}
              sx={{ borderTop: "1px solid rgba(226,232,240,0.8)", color: NJIM.textSecondary, "& .MuiSvgIcon-root": { color: NJIM.textSecondary } }}
            />
          )}
        </Box>
      </Box>

      {/* ── Confirm Remove Dialog ── */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, color: NJIM.textPrimary }}>{t("itemControl.removeControl")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: NJIM.textSecondary }}>
            {t("itemControl.removeControlConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button type="button" onClick={() => setConfirmDeleteId(null)} variant="outlined" sx={{ textTransform: "none", fontWeight: 600, borderColor: "rgba(226,232,240,0.9)", color: NJIM.textPrimary, borderRadius: "8px" }}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleRemoveControl}
            disabled={actionLoading}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", background: NJIM.danger, color: NJIM.white, px: 2.5, "&:hover": { background: NJIM.danger }, "&.Mui-disabled": { opacity: 0.55, color: NJIM.white, background: NJIM.danger } }}
          >
            {actionLoading ? <CircularProgress size={15} sx={{ color: NJIM.white }} /> : t("itemControl.remove")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════
          Add / Edit Control Modal
      ═══════════════════════════════════════ */}
      <Dialog
        open={!!modal}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          component: "form",
          onSubmit: (e: React.FormEvent) => {
            e.preventDefault();
            handleSubmitControl();
          },
          sx: {
            borderRadius: "16px",
            background: NJIM.white,
            boxShadow: "0 24px 60px rgba(2,6,23,0.18)",
          },
        }}
      >
        {/* ── Modal Header ── */}
        <DialogTitle
          sx={{
            px: 3,
            pt: 2.5,
            pb: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: NJIM.textPrimary }}>
              {modal?.mode === "add" ? t("itemControl.addItemControl") : t("itemControl.editItemControl")}
            </Typography>
            <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mt: 0.3 }}>
              {t("itemControl.configureControl")}
            </Typography>
          </Box>
          <IconButton onClick={closeModal} size="small" sx={{ color: NJIM.textMuted, mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {modal && (
            <>
              {/* ── Item Details Card ── */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid rgba(226,232,240,0.9)",
                  background: NJIM.muted,
                  mb: 3,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, mb: 1.5 }}>
                  {t("itemControl.itemDetails")}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  {[
                    { icon: <TagIcon sx={{ fontSize: 15, color: NJIM.textMuted }} />, label: t("itemControl.colItemCode"), value: modal.group.code },
                    { icon: <DescriptionOutlinedIcon sx={{ fontSize: 15, color: NJIM.textMuted }} />, label: t("itemControl.colItemDesc"), value: modal.group.description },
                    { icon: <InventoryOutlinedIcon sx={{ fontSize: 15, color: NJIM.textMuted }} />, label: t("itemControl.availableQuantity"), value: `${modal.group.totalQty.toLocaleString("en-US")} ${t("itemControl.units")}` },
                    { icon: <LocationOnOutlinedIcon sx={{ fontSize: 15, color: NJIM.textMuted }} />, label: t("itemControl.site"), value: modal.group.stockSite || "—" },
                  ].map((field) => (
                    <Box key={field.label}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                        {field.icon}
                        <Typography sx={{ fontSize: 11, color: NJIM.textMuted, fontWeight: 500 }}>{field.label}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary }}>{field.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* ── Restriction Sections (independent toggles, AND logic) ── */}
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: NJIM.textPrimary, mb: 0.5 }}>
                {t("itemControl.restrictions")}
              </Typography>
              <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mb: 1.5 }}>
                {t("itemControl.restrictionsHint")}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

                {/* ── Section 1: Quantity Restriction ── */}
                <Box
                  sx={{
                    borderRadius: "12px",
                    border: formConsumptionEnabled ? `2px solid ${NJIM.warning}` : `1.5px solid ${NJIM.border}`,
                    background: formConsumptionEnabled ? "rgba(245,158,11,0.04)" : NJIM.white,
                    p: 1.75,
                    transition: "all 0.15s",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setFormConsumptionEnabled((v) => !v)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ color: NJIM.warning, display: "flex" }}><SpeedOutlinedIcon sx={{ fontSize: 20 }} /></Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary }}>{t("itemControl.quantityRestriction")}</Typography>
                        <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 0.2 }}>{t("itemControl.quantityRestrictionDesc")}</Typography>
                      </Box>
                    </Box>
                    {/* Toggle switch */}
                    <Box
                      sx={{
                        width: 40, height: 22, borderRadius: "11px", position: "relative", flexShrink: 0,
                        background: formConsumptionEnabled ? NJIM.warning : "rgba(203,213,225,0.8)",
                        transition: "background 0.2s",
                      }}
                    >
                      <Box sx={{ width: 16, height: 16, borderRadius: "50%", background: NJIM.white, position: "absolute", top: 3, insetInlineStart: formConsumptionEnabled ? 21 : 3, transition: "inset-inline-start 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </Box>
                  </Box>
                  {formConsumptionEnabled && (
                    <Box sx={{ mt: 1.5, marginInlineStart: 4 }} onClick={(e) => e.stopPropagation()}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.textPrimary, mb: 0.75 }}>{t("itemControl.maxQtyPerCustomerBatch")}</Typography>
                      <TextField
                        fullWidth size="small" type="number" value={formMaxQty}
                        onChange={(e) => setFormMaxQty(e.target.value)}
                        placeholder={t("itemControl.enterMaxQty")}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: NJIM.white } }}
                      />
                    </Box>
                  )}
                </Box>

                {/* ── Section 2: Customer Restriction ── */}
                <Box
                  sx={{
                    borderRadius: "12px",
                    border: formCustomerEnabled ? `2px solid ${NJIM.info}` : "1.5px solid rgba(226,232,240,0.9)",
                    background: formCustomerEnabled ? "rgba(59,130,246,0.04)" : NJIM.white,
                    p: 1.75,
                    transition: "all 0.15s",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setFormCustomerEnabled((v) => !v)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ color: NJIM.info, display: "flex" }}><GroupOutlinedIcon sx={{ fontSize: 20 }} /></Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary }}>{t("itemControl.customerRestriction")}</Typography>
                        <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 0.2 }}>{t("itemControl.customerRestrictionDesc")}</Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 40, height: 22, borderRadius: "11px", position: "relative", flexShrink: 0,
                        background: formCustomerEnabled ? NJIM.info : "rgba(203,213,225,0.8)",
                        transition: "background 0.2s",
                      }}
                    >
                      <Box sx={{ width: 16, height: 16, borderRadius: "50%", background: NJIM.white, position: "absolute", top: 3, insetInlineStart: formCustomerEnabled ? 21 : 3, transition: "inset-inline-start 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </Box>
                  </Box>
                  {formCustomerEnabled && (
                    <Box sx={{ mt: 1.5, marginInlineStart: 4 }} onClick={(e) => e.stopPropagation()}>
                      {/* Header row: label + Select All / Remove All */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.textPrimary }}>
                          {t("itemControl.selectCustomers")}
                          {formCustomers.length > 0 && (
                            <Box component="span" sx={{ marginInlineStart: 1, px: 0.8, py: 0.2, borderRadius: "5px", background: NJIM.mintWash, color: BRAND, fontSize: 11, fontWeight: 700 }}>
                              {t("itemControl.selectedCount", { count: formCustomers.length })}
                            </Box>
                          )}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75 }}>
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Select All adds the current search matches (keeps things fast —
                              // never bulk-adds the entire customer base in one click).
                              setFormCustomers((prev) => {
                                const newIds = liveCustomers.map((c) => c.id).filter((id) => !prev.includes(id));
                                return [...prev, ...newIds];
                              });
                            }}
                            sx={{ fontSize: 11, fontWeight: 600, color: BRAND, cursor: "pointer", px: 1, py: 0.4, borderRadius: "6px", border: `1px solid rgba(22,45,58,0.2)`, "&:hover": { background: NJIM.mintWash } }}
                          >
                            {t("itemControl.selectAll")}
                          </Box>
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormCustomers([]);
                            }}
                            sx={{ fontSize: 11, fontWeight: 600, color: NJIM.danger, cursor: "pointer", px: 1, py: 0.4, borderRadius: "6px", border: "1px solid rgba(239,68,68,0.2)", "&:hover": { background: "rgba(239,68,68,0.06)" } }}
                          >
                            {t("itemControl.removeAll")}
                          </Box>
                        </Box>
                      </Box>
                      {/* Search by name or customer code — server-side */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, border: "1px solid rgba(226,232,240,0.9)", borderRadius: "8px", background: NJIM.white, px: 1.2, mb: 1, height: 34 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SearchIcon sx={{ color: NJIM.textMuted, fontSize: 15 }} />
                        <InputBase
                          placeholder={t("itemControl.searchByNameOrCode")}
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.preventDefault();
                          }}
                          sx={{ fontSize: 12, flex: 1 }}
                        />
                        {customerSearchLoading && <CircularProgress size={12} sx={{ color: NJIM.textMuted }} />}
                      </Box>
                      <Box sx={{ border: "1px solid rgba(226,232,240,0.9)", borderRadius: "10px", background: NJIM.white, maxHeight: 220, overflowY: "auto" }}>
                        {(() => {
                          const query = customerSearch.trim();
                          const isSearching = query.length >= 1;
                          // When not searching, only show already-selected customers (keeps the
                          // list light — thousands of rows are never rendered at once).
                          const selectedPool = customers.filter((c) => formCustomers.includes(c.id));
                          const pool = isSearching ? liveCustomers : selectedPool;

                          if (!isSearching && selectedPool.length === 0) {
                            return <Typography sx={{ p: 2, fontSize: 13, color: NJIM.textMuted, textAlign: "center" }}>{t("itemControl.typeToSearch")}</Typography>;
                          }
                          if (isSearching && !customerSearchLoading && liveCustomers.length === 0) {
                            return <Typography sx={{ p: 2, fontSize: 13, color: NJIM.textMuted, textAlign: "center" }}>{t("itemControl.noResultsFor", { query })}</Typography>;
                          }

                          // Sort: selected customers pinned to top (ticket 3)
                          const sorted = [...pool].sort((a, b) => {
                            const aSelected = formCustomers.includes(a.id);
                            const bSelected = formCustomers.includes(b.id);
                            if (aSelected && !bSelected) return -1;
                            if (!aSelected && bSelected) return 1;
                            return 0;
                          });

                          return sorted.map((c, idx) => (
                            <Box
                              key={c.id}
                              onClick={() => toggleCustomer(c.id)}
                              sx={{
                                display: "flex", alignItems: "center", gap: 1.2, px: 1.5, py: 1, cursor: "pointer",
                                borderBottom: idx < sorted.length - 1 ? "1px solid rgba(226,232,240,0.5)" : "none",
                                background: formCustomers.includes(c.id) ? "rgba(10,61,120,0.05)" : "transparent",
                                "&:hover": { background: "rgba(248,250,252,0.9)" },
                              }}
                            >
                              <Checkbox checked={formCustomers.includes(c.id)} size="small" sx={{ p: 0, color: NJIM.textMuted, "&.Mui-checked": { color: BRAND } }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.textPrimary }}>{c.name}</Typography>
                                {c.customerNo && <Typography sx={{ fontSize: 11, color: NJIM.textMuted }}>{c.customerNo}</Typography>}
                              </Box>
                              {formCustomers.includes(c.id) && (
                                <Box sx={{ fontSize: 10, fontWeight: 700, color: NJIM.success, px: 0.8, py: 0.2, borderRadius: "4px", background: "rgba(5,150,105,0.08)", flexShrink: 0 }}>
                                  {t("itemControl.authorized")}
                                </Box>
                              )}
                            </Box>
                          ));
                        })()}
                      </Box>
                    </Box>
                  )}
                </Box>

              </Box>

              {/* ── AND logic indicator ── */}
              {formConsumptionEnabled && formCustomerEnabled && (
                <Box sx={{ mt: 1, p: 1.2, borderRadius: "8px", background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: NJIM.info, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: NJIM.info, fontWeight: 600 }}>
                    {t("itemControl.andLogicActive")}
                  </Typography>
                </Box>
              )}

              {/* ── Batch Selection ── */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, mb: 0.5 }}>{t("itemControl.applyToBatches")}</Typography>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mb: 1 }}>{t("itemControl.applyToBatchesHint")}</Typography>
                {/* Mode toggle */}
                <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                  {(["all", "specific"] as const).map((mode) => (
                    <Box
                      key={mode}
                      onClick={() => {
                        setFormBatchMode(mode);
                        setFormSelectedBatches([]);
                      }}
                      sx={{
                        px: 2, py: 0.75, borderRadius: "8px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                        border: formBatchMode === mode ? `1.5px solid ${BRAND}` : "1.5px solid rgba(226,232,240,0.9)",
                        background: formBatchMode === mode ? `rgba(10,61,120,0.07)` : NJIM.white,
                        color: formBatchMode === mode ? BRAND : NJIM.textSecondary,
                        transition: "all 0.15s",
                      }}
                    >
                      {mode === "all" ? t("itemControl.allBatches") : t("itemControl.specificBatches")}
                    </Box>
                  ))}
                </Box>
                {/* Specific batch picker */}
                {formBatchMode === "specific" && (
                  <Box sx={{ border: "1px solid rgba(226,232,240,0.9)", borderRadius: "10px", background: NJIM.white, maxHeight: 180, overflowY: "auto" }}>
                    {batchesLoading ? (
                      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={14} sx={{ color: NJIM.textMuted }} />
                        <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>{t("itemControl.loadingBatches")}</Typography>
                      </Box>
                    ) : modalBatches.length === 0 ? (
                      <Typography sx={{ p: 2, fontSize: 13, color: NJIM.textMuted, textAlign: "center" }}>{t("itemControl.noBatches")}</Typography>
                    ) : (
                      modalBatches.map((b, idx) => {
                        const selected = formSelectedBatches.includes(b.lotNumber);
                        return (
                          <Box
                            key={b.lotNumber}
                            onClick={() => setFormSelectedBatches((prev) =>
                              selected ? prev.filter((x) => x !== b.lotNumber) : [...prev, b.lotNumber]
                            )}
                            sx={{
                              display: "flex", alignItems: "center", gap: 1.2, px: 1.5, py: 1, cursor: "pointer",
                              borderBottom: idx < modalBatches.length - 1 ? "1px solid rgba(226,232,240,0.5)" : "none",
                              background: selected ? "rgba(10,61,120,0.04)" : "transparent",
                              "&:hover": { background: "rgba(248,250,252,0.9)" },
                            }}
                          >
                            <Checkbox checked={selected} size="small" sx={{ p: 0, color: NJIM.textMuted, "&.Mui-checked": { color: BRAND } }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.textPrimary }}>{b.lotNumber}</Typography>
                              {b.expirationDate && (
                                <Typography sx={{ fontSize: 11, color: NJIM.textMuted }}>
                                  Exp: {new Date(b.expirationDate).toISOString().split("T")[0]}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                )}
                {formBatchMode === "specific" && formSelectedBatches.length === 0 && !batchesLoading && (
                  <Typography sx={{ fontSize: 12, color: NJIM.danger, mt: 0.5 }}>{t("itemControl.selectAtLeastOneBatch")}</Typography>
                )}
              </Box>

              {/* ── Apply to Sites ── */}
              {modalAvailableSites.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, mb: 0.5 }}>{t("itemControl.applyToSites")}</Typography>
                  <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mb: 1 }}>{t("itemControl.selectSitesHint")}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {modalAvailableSites.map((site) => {
                      const active = formSites.includes(site);
                      return (
                        <Box
                          key={site}
                          onClick={() => setFormSites((prev) => active ? prev.filter((s) => s !== site) : [...prev, site])}
                          sx={{
                            display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.7,
                            borderRadius: "8px", cursor: "pointer",
                            border: active ? `1.5px solid ${BRAND}` : "1.5px solid rgba(226,232,240,0.9)",
                            background: active ? NJIM.mintWash : NJIM.white,
                            transition: "all 0.15s",
                          }}
                        >
                          <LocationOnOutlinedIcon sx={{ fontSize: 14, color: active ? BRAND : NJIM.textMuted }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: active ? BRAND : NJIM.textSecondary }}>{site}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {formSites.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: NJIM.danger, mt: 0.5 }}>{t("itemControl.atLeastOneSite")}</Typography>
                  )}
                </Box>
              )}

              {/* ── Note ── */}
              <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)" }}>
                <Typography sx={{ fontSize: 13, color: NJIM.info }}>
                  <strong>{t("itemControl.note")}:</strong> {t("itemControl.controlNote")}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            type="button"
            onClick={closeModal}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: NJIM.textPrimary,
              borderColor: "rgba(226,232,240,0.9)",
              borderRadius: "9px",
              px: 2.5,
              "&:hover": { borderColor: NJIM.textMuted, background: "rgba(248,250,252,0.8)" },
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={actionLoading || !isModalValid()}
            startIcon={
              actionLoading ? (
                <CircularProgress size={15} sx={{ color: NJIM.white }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "9px",
              background: `${NJIM.walletHero}`,
              color: NJIM.white,
              px: 2.5,
              "&.Mui-disabled": { opacity: 0.55, color: NJIM.white, background: NJIM.textMuted },
              "&:hover": { opacity: 0.92 },
            }}
          >
            {modal?.mode === "add" ? t("itemControl.addControl") : t("itemControl.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemControlRelease;
